import React, { useEffect, useState } from 'react';
import { apiGet, apiPut } from '@/utils/fetchInterceptor';

interface ConfigResponse { success: boolean; data?: Record<string, unknown>; message?: string }

/** Uses the existing Hub session, API base path and configuration persistence. */
export default function FunnelSettings() {
  const [text, setText] = useState('');
  const [saved, setSaved] = useState('');
  const [busy, setBusy] = useState(true);
  const [message, setMessage] = useState('');
  const [error, setError] = useState(false);
  useEffect(() => {
    let active = true;
    void apiGet<ConfigResponse>('/funnel/config').then((result) => {
      if (!active) return;
      if (result.success && result.data) {
        const value = JSON.stringify(result.data, null, 2);
        setText(value); setSaved(value);
      } else { setError(true); setMessage(result.message || 'Unable to load Funnel settings'); }
      setBusy(false);
    });
    return () => { active = false; };
  }, []);
  async function save() {
    let value: unknown;
    try { value = JSON.parse(text); }
    catch { setError(true); setMessage('Enter valid JSON before saving.'); return; }
    setBusy(true); setMessage(''); setError(false);
    const result = await apiPut<ConfigResponse>('/funnel/config', value);
    if (result.success && result.data) {
      const normalized = JSON.stringify(result.data, null, 2);
      setText(normalized); setSaved(normalized); setMessage('Funnel settings saved. Existing sessions use the new policy on their next request.');
    } else { setError(true); setMessage(result.message || 'Unable to save Funnel settings'); }
    setBusy(false);
  }
  return (
    <section className="bg-white shadow rounded-lg mb-6 dashboard-card p-6" aria-labelledby="funnel-heading">
      <h2 id="funnel-heading" className="font-semibold text-gray-800">MCP Funnel — progressive tool discovery</h2>
      <p className="mt-2 text-sm text-gray-600">Use <code>/mcp/$funnel</code> or <code>/mcp/$funnel/GROUP</code> under your Hub base path. The same keys, groups, upstreams and activity logs apply. Ordinary and smart routes are unchanged.</p>
      <p className="mt-2 text-sm text-gray-600">Selections are private to each connection. Use fully qualified Hub tool names in patterns. An asterisk matches any characters; other characters are literal. Saving replaces this entire configuration, including named toolsets.</p>
      <label htmlFor="funnel-config" className="block mt-4 text-sm font-medium text-gray-700">Funnel configuration (JSON)</label>
      <textarea id="funnel-config" value={text} onChange={(event) => setText(event.target.value)} disabled={busy}
        spellCheck={false} rows={17} className="mt-2 w-full border rounded-md p-3 font-mono text-sm bg-gray-50 text-gray-900 disabled:opacity-60" />
      <div className="mt-3 flex flex-wrap gap-3">
        <button type="button" onClick={() => void save()} disabled={busy || !saved || text === saved}
          className="px-4 py-2 bg-blue-600 text-white rounded-md disabled:opacity-50">{busy ? 'Loading…' : 'Save Funnel settings'}</button>
        <button type="button" onClick={() => { setText(saved); setMessage(''); }} disabled={busy || !saved || text === saved}
          className="px-4 py-2 border rounded-md text-gray-700 disabled:opacity-50">Discard edits</button>
      </div>
      {message && <p role={error ? 'alert' : 'status'} className={`mt-3 text-sm ${error ? 'text-red-700' : 'text-green-700'}`}>{message}</p>}
    </section>
  );
}
